import { useEffect, useState, type FormEvent } from 'react';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { Stripe } from '@stripe/stripe-js';

import type { AppPlan } from '../pages/app/appPlans';
import { isStripeConfigured } from '../stripeConfig';
import { ModalCloseButton } from './InfoModals';

export interface SubscriptionPaymentSubmission {
  paymentMethodId: string;
  requestId: string;
  stripe: Stripe;
}

interface Props {
  plan: AppPlan | null;
  isSubmitting: boolean;
  error: string;
  onCancel: () => void;
  onError: (message: string) => void;
  onSubmit: (submission: SubscriptionPaymentSubmission) => Promise<void>;
}

const createRequestId = (): string => typeof window.crypto?.randomUUID === 'function'
  ? window.crypto.randomUUID()
  : `subscription-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;

const formatAmount = (amountUsd: number): string => new Intl.NumberFormat(
  'en-US', { style: 'currency', currency: 'USD' }
).format(amountUsd);

export default function SubscriptionCheckoutModal({ plan, isSubmitting, error, onCancel, onError, onSubmit }: Props) {
  const stripe = useStripe();
  const elements = useElements();
  const [isCardComplete, setIsCardComplete] = useState(false);
  const [cardError, setCardError] = useState('');

  useEffect(() => {
    if (plan) {
      setIsCardComplete(false);
      setCardError('');
    }
  }, [plan]);

  if (!plan) return null;

  const cancel = () => {
    if (!isSubmitting) onCancel();
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCardError('');
    onError('');

    if (!isStripeConfigured) {
      onError('Stripe is not configured. Set stripePublishableKey in runtime-config.js and reload the page.');
      return;
    }

    const card = elements?.getElement(CardElement);
    if (!stripe || !card) {
      onError('The secure payment form is still loading. Please try again.');
      return;
    }
    if (!isCardComplete) {
      setCardError('Complete the card details before subscribing.');
      return;
    }

    const result = await stripe.createPaymentMethod({ type: 'card', card });
    if (result.error || !result.paymentMethod) {
      setCardError(result.error?.message ?? 'Stripe could not create the payment method.');
      return;
    }

    await onSubmit({ paymentMethodId: result.paymentMethod.id, requestId: createRequestId(), stripe });
  };

  return (
    <div className="modal-overlay" onClick={cancel}>
      <form
        className="modal-content subscription-checkout-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="subscription-checkout-title"
        onClick={(event) => event.stopPropagation()}
        onSubmit={(event) => void submit(event)}
      >
        <div className="modal-header">
          <h2 id="subscription-checkout-title">Subscribe to {plan.name}</h2>
          <ModalCloseButton onClose={cancel} disabled={isSubmitting} />
        </div>
        <div className="modal-body subscription-checkout-body">
          <div className="subscription-checkout-summary">
            <span>Monthly total</span>
            <strong>{formatAmount(plan.priceUsd)}</strong>
          </div>
          <p>Your subscription renews monthly until canceled. Card details are handled securely by Stripe and are never sent to Havu.</p>
          {!isStripeConfigured && <p className="pricing-email-error" role="alert">Stripe is not configured. Set stripePublishableKey in runtime-config.js and reload the page.</p>}
          <label className="field">
            <span>Card details</span>
            <div className="subscription-card-field">
              <CardElement
                options={{
                  hidePostalCode: false,
                  style: {
                    base: { color: '#f4f4f5', fontFamily: 'Inter, system-ui, sans-serif', fontSize: '16px', '::placeholder': { color: '#71717a' } },
                    invalid: { color: '#f87171' }
                  }
                }}
                onChange={(event) => {
                  setIsCardComplete(event.complete);
                  setCardError(event.error?.message ?? '');
                }}
              />
            </div>
          </label>
          {(cardError || error) && <p className="pricing-email-error" role="alert">{cardError || error}</p>}
        </div>
        <div className="modal-footer pricing-email-footer">
          <button type="button" className="secondary-button" disabled={isSubmitting} onClick={cancel}>Cancel</button>
          <button type="submit" className="primary-button" disabled={isSubmitting || !isStripeConfigured || !stripe}>
            {isSubmitting ? 'Activating subscription...' : `Subscribe for ${formatAmount(plan.priceUsd)}/month`}
          </button>
        </div>
      </form>
    </div>
  );
}
